import { useGrantConsent } from '@/services'
import { ConsentChallengeResponse, RichRequiredAction, User } from '@/types'
import {
  buildGrants,
  buildInitialToggles,
  buildPermissionsPayload,
  resolveExistingGrants,
} from '@/pages/external/consentPermissions'

/**
 * The answer given on the member's behalf when no consent screen is shown: every required key on,
 * and each optional key as the member already left it (off if they never answered). Keyed by the
 * server's descriptors, so a newly required consent is granted without a web release.
 */
const buildAutoGrant = (consent: ConsentChallengeResponse): Record<string, boolean> =>
  buildGrants(
    consent.permissions,
    buildInitialToggles(consent.permissions, resolveExistingGrants(consent.existingGrants, consent.existingPermissions))
  )

export const extractConsentChallenge = (error: unknown): ConsentChallengeResponse | null => {
  const err = error as { response?: { status?: number; data?: { consentToken?: string } } }
  if (err?.response?.status === 428 && err?.response?.data?.consentToken) {
    return err.response.data as ConsentChallengeResponse
  }
  return null
}

export const useAutoGrantConsent = () => {
  const { mutate: grantConsent, isLoading: isGrantingConsent } = useGrantConsent()

  const autoGrantConsent = (
    consent: ConsentChallengeResponse,
    onSuccess: (accessToken: string, refreshToken: string, user: User, requirements?: RichRequiredAction[]) => void,
    onError: (message: string) => void
  ) => {
    const grants = buildAutoGrant(consent)
    grantConsent(
      {
        consentToken: consent.consentToken,
        // `grants` is what a current server reads; `permissions` keeps an older one working.
        grants,
        permissions: buildPermissionsPayload(grants),
        useExistingConsent: false,
      },
      {
        onSuccess: (data) => {
          onSuccess(data.result.accessToken, data.result.refreshToken, data.result.user, data.result.requirements)
        },
        onError: (error: any) => {
          const message = error?.response?.data?.message || 'Unable to complete login. Please try again.'
          onError(message)
        },
      }
    )
  }

  return { autoGrantConsent, isGrantingConsent }
}
